import { useCallback, useEffect, useState } from "react";
import { ArrowCounterClockwise, Trash, ForkKnife } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const plural = (n) => `${n} recipe${n === 1 ? "" : "s"}`;

function Row({ r, busy, onRestore, onPurge }) {
  return (
    <li className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-rf-ink/10" data-testid={`recipe-trash-row-${r.recipe_id}`}>
      <div className="w-14 h-14 rounded-xl bg-rf-cream-alt overflow-hidden shrink-0 flex items-center justify-center">
        {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : <ForkKnife size={20} className="text-rf-orange" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-rf-ink truncate">{r.title || "Untitled"}</p>
        <p className="text-xs text-rf-ink-soft truncate">
          {r.cookbook_title ? `From ${r.cookbook_title}` : "Goes back to your Inbox"} ·{" "}
          <span className={r.days_left <= 3 ? "text-red-700 font-bold" : ""}>{r.days_left === 0 ? "Deleting today" : `${r.days_left} day${r.days_left === 1 ? "" : "s"} left`}</span>
        </p>
      </div>
      <button type="button" disabled={busy} onClick={() => onRestore([r.recipe_id])} data-testid={`recipe-trash-restore-${r.recipe_id}`} className="btn-amber !py-2 !px-3 text-sm" aria-label="Restore">
        <ArrowCounterClockwise size={16} /> <span className="hidden sm:inline">Restore</span>
      </button>
      <button type="button" disabled={busy} onClick={() => onPurge(r)} data-testid={`recipe-trash-purge-${r.recipe_id}`} className="w-10 h-10 rounded-full flex items-center justify-center text-red-700 hover:bg-red-50" aria-label="Delete forever">
        <Trash size={18} />
      </button>
    </li>
  );
}

export function RecipeTrash({ onCount }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/me/recipe-trash").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (rows) onCount?.(rows.length); }, [rows, onCount]);

  const restore = async (ids) => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/recipe-trash/restore", { recipe_ids: ids });
      toast.success(`${plural(data.restored)} restored${data.to_inbox ? ` (${data.to_inbox} to your Inbox, as the old cookbook is gone)` : ""}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    setBusy(false);
  };
  const purge = async (r) => {
    if (!window.confirm(`Delete "${r.title}" forever? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.post("/me/recipe-trash/purge", { recipe_ids: [r.recipe_id] });
      toast.success("Deleted forever");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setBusy(false);
  };

  if (!rows || rows.length === 0) return null;
  return (
    <section className="mt-12" data-testid="recipe-trash">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display font-black text-2xl text-rf-ink">Recipes ({rows.length})</h2>
        {rows.length > 1 && (
          <button type="button" disabled={busy} onClick={() => restore(rows.map((r) => r.recipe_id))} data-testid="recipe-trash-restore-all" className="btn-outline-ink !py-2 !px-4 text-sm">
            <ArrowCounterClockwise size={16} /> Restore all
          </button>
        )}
      </div>
      <ul className="space-y-3" data-testid="recipe-trash-list">
        {rows.map((r) => <Row key={r.recipe_id} r={r} busy={busy} onRestore={restore} onPurge={purge} />)}
      </ul>
    </section>
  );
}
